import { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Heart, Share2, Star, Users, BedDouble, Bath, Wifi, Utensils,
  Waves, Flame, Car, Laptop, Mountain, PawPrint, Droplets, Grid3x3, LogIn,
  LogOut, Moon, Cigarette, PartyPopper, IdCard,
} from "lucide-react";
import { DETAIL } from "@/lib/testIds";
import { HOUSE_RULES, FAQS } from "@/data/properties";
import { toast } from "sonner";
import GalleryLightbox from "@/components/GalleryLightbox";
import BookingCalendar from "@/components/BookingCalendar";
import { useProperty } from "@/hooks/useApiData";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const AMENITY_ICONS = {
  "Wi-Fi": Wifi, Kitchen: Utensils, Pool: Waves, "Hot tub": Droplets,
  Fireplace: Flame, "Free parking": Car, Workspace: Laptop,
  "Mountain view": Mountain, "Ocean view": Waves, "Pets allowed": PawPrint,
};

const RULE_ICONS = {
  "clock-in": LogIn, "clock-out": LogOut, guests: Users, quiet: Moon,
  smoking: Cigarette, parties: PartyPopper, pets: PawPrint, id: IdCard,
};

const galleryGridClass = (count) => {
  if (count <= 1) return "grid h-[360px] grid-cols-1 gap-1.5 md:h-[520px] md:grid-cols-1 md:grid-rows-1 md:gap-3";
  if (count === 2) return "grid grid-cols-2 grid-rows-[240px_160px] gap-1.5 md:h-[520px] md:grid-cols-3 md:grid-rows-1 md:gap-3";
  return "grid grid-cols-2 grid-rows-[240px_120px] gap-1.5 md:h-[520px] md:grid-cols-4 md:grid-rows-2 md:gap-3";
};

const galleryItemClass = (count, index) => {
  const mobile = index === 0
    ? "col-span-2"
    : index === 1 && count === 2
      ? "col-span-2"
      : index > 2
        ? "hidden md:block"
        : "";

  if (count <= 1) return "col-span-1 row-span-1";
  if (count === 2 && index === 0) return `${mobile} md:col-span-2 md:row-span-1`;
  if (count === 2) return `${mobile} md:col-span-1 md:row-span-1`;
  if (index === 0) return `${mobile} md:col-span-2 md:row-span-2`;
  if (count === 3) return `${mobile} md:col-span-2 md:row-span-1`;
  if (count === 4 && index === 1) return `${mobile} md:col-span-2 md:row-span-1`;
  return `${mobile} md:col-span-1 md:row-span-1`;
};

export const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { property, loading, notFound, error } = useProperty(id);
  const [saved, setSaved] = useState(false);
  const [dates, setDates] = useState(undefined);
  const [guests, setGuests] = useState(2);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const widgetRef = useRef(null);

  useEffect(() => {
    if (property?.guests && guests > property.guests) setGuests(property.guests);
  }, [property, guests]);

  const pricing = useMemo(() => {
    const nights = dates?.from && dates?.to ? Math.max(0, Math.round((dates.to - dates.from) / 86400000)) : 0;
    const nightly = Number(property?.price || 0);
    const subtotal = nightly * nights;
    return { nights, subtotal, total: subtotal };
  }, [dates, property]);

  const openLightbox = (i = 0) => {
    setLightboxIndex(i);
    setLightboxOpen(true);
  };

  const onCheckAvailability = () => {
    if (!dates?.from || !dates?.to || pricing.nights < 1) {
      toast.error("Choose valid check-in and check-out dates first.");
      widgetRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const params = new URLSearchParams({
      check_in: format(dates.from, "yyyy-MM-dd"),
      check_out: format(dates.to, "yyyy-MM-dd"),
      guests: String(guests),
    });
    navigate(`/booking/review/${property.slug || id}?${params.toString()}`);
  };

  const onMobileCheckAvailability = () => {
    if (dates?.from && dates?.to && pricing.nights > 0) {
      onCheckAvailability();
      return;
    }
    widgetRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading) return <PropertyDetailSkeleton />;

  if (error && !notFound) {
    return (
      <main className="mx-auto max-w-[900px] px-6 py-40 text-center md:px-10">
        <h1 className="font-display text-5xl md:text-6xl">We couldn’t load this stay.</h1>
        <p className="mt-4 text-muted-foreground">Please refresh the page and try again.</p>
        <Link to="/properties" className="mt-8 inline-flex items-center gap-2 text-sm underline"><ArrowLeft size={14} /> Back to all stays</Link>
      </main>
    );
  }

  if (!property) {
    return (
      <main className="mx-auto max-w-[900px] px-6 py-40 text-center md:px-10">
        <h1 className="font-display text-6xl">Not found.</h1>
        <p className="mt-4 text-muted-foreground">This stay may have wandered off.</p>
        <Link to="/properties" className="mt-8 inline-flex items-center gap-2 text-sm underline"><ArrowLeft size={14} /> Back to all stays</Link>
      </main>
    );
  }

  const galleryImages = (property.images || []).filter(Boolean);
  const visibleGalleryImages = galleryImages.slice(0, 5);

  const BookingWidget = ({ mobile = false }) => (
    <div ref={mobile ? widgetRef : undefined} data-testid={mobile ? undefined : DETAIL.bookingWidget} className="border border-foreground/10 bg-card p-6 md:p-8">
      <div className="mb-6 flex items-baseline justify-between"><p><span className="font-display text-3xl md:text-4xl">${property.price}</span><span className="ml-1 text-sm text-muted-foreground">/ night</span></p><p className="inline-flex items-center gap-1 text-sm"><Star size={12} className="fill-foreground stroke-foreground" />{property.rating.toFixed(2)}</p></div>
      <BookingCalendar propertySlug={property.slug || id} value={dates} onChange={setDates} />
      <label className="mt-4 block border border-foreground/10 p-4"><p className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">Guests</p><select value={guests} onChange={(event) => setGuests(Number(event.target.value))} className="mt-1 w-full bg-transparent text-sm focus:outline-none">{Array.from({ length: property.guests }, (_, index) => index + 1).map((count) => <option key={count} value={count}>{count} guest{count > 1 ? "s" : ""}</option>)}</select></label>
      {!mobile && <button type="button" onClick={onCheckAvailability} className="mt-5 w-full rounded-full bg-primary py-4 text-sm font-medium text-primary-foreground transition-transform hover:-translate-y-0.5">Check availability</button>}
      {pricing.nights > 0 ? <div className="mt-5 space-y-3 text-sm"><div className="flex justify-between"><span>${property.price} × {pricing.nights} nights</span><span>${pricing.subtotal}</span></div><div className="flex justify-between border-t border-foreground/10 pt-3 font-medium"><span>Estimated total</span><span className="font-display text-xl">${pricing.total}</span></div></div> : <p className="mt-4 text-center text-xs text-muted-foreground">Select dates to check availability and see the full price.</p>}
    </div>
  );

  return (
    <main data-testid={DETAIL.root} className="pb-32 md:pb-32">
      <div className="mx-auto max-w-[1400px] px-5 pt-8 md:px-10 md:pt-16">
        <Link to="/properties" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-foreground md:text-xs"><ArrowLeft size={12} /> All stays</Link>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-4 md:mt-6 md:gap-6">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground md:text-xs">{property.location}</p>
            <h1 data-testid={DETAIL.title} className="mt-2 font-display text-4xl font-medium leading-[0.95] tracking-tight md:mt-3 md:text-7xl">{property.name}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground md:mt-4 md:gap-5 md:text-sm">
              <span className="inline-flex items-center gap-1 text-foreground"><Star size={14} className="fill-foreground stroke-foreground" />{property.rating.toFixed(2)}</span>
              <span>· {property.reviews} reviews</span><span>· Superhost</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button type="button" onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success("Link copied"); }} className="inline-flex items-center gap-2 text-sm text-foreground/70 hover:text-foreground"><Share2 size={14} /> Share</button>
            <button type="button" onClick={() => setSaved((value) => !value)} className="inline-flex items-center gap-2 text-sm text-foreground/70 hover:text-foreground"><Heart size={14} className={saved ? "fill-primary stroke-primary" : ""} /> Save</button>
          </div>
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-foreground/10 bg-background/95 backdrop-blur-xl md:hidden">
        <div className="mx-auto flex max-w-[600px] items-center justify-between gap-4 px-5 py-3 pb-[calc(env(safe-area-inset-bottom)+12px)]">
          <p><span className="font-display text-2xl">${property.price}</span><span className="ml-1 text-xs text-muted-foreground">/ night</span></p>
          <button type="button" onClick={onMobileCheckAvailability} className="min-w-[160px] rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground">Check availability</button>
        </div>
      </div>

      <section className="mx-auto mt-6 grid max-w-[1400px] grid-cols-1 gap-10 px-5 md:mt-10 md:grid-cols-[minmax(0,1.62fr)_minmax(330px,0.88fr)] md:items-start md:gap-14 md:px-10">
        <div className="min-w-0">
          <div data-testid={DETAIL.gallery} className="relative">
            {visibleGalleryImages.length > 0 ? (
              <div className={`relative ${galleryGridClass(visibleGalleryImages.length)}`}>
                {visibleGalleryImages.map((src, index) => (
                  <button
                    key={`${src}-${index}`}
                    type="button"
                    onClick={() => openLightbox(index)}
                    className={`group relative overflow-hidden ${galleryItemClass(visibleGalleryImages.length, index)}`}
                  >
                    <img src={src} alt="" className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.03]" />
                  </button>
                ))}
              </div>
            ) : (
              <div className="grid h-[360px] place-items-center border border-dashed border-foreground/15 bg-muted/20 text-sm text-muted-foreground md:h-[520px]">Photos coming soon.</div>
            )}
            {galleryImages.length > 0 && (
              <button type="button" onClick={() => openLightbox(0)} className="absolute bottom-3 right-3 inline-flex items-center gap-2 rounded-full border border-foreground/20 bg-background/90 px-4 py-2 text-xs font-medium backdrop-blur md:bottom-6 md:right-6"><Grid3x3 size={14} /> Show all {galleryImages.length} photo{galleryImages.length === 1 ? "" : "s"}</button>
            )}
          </div>

          <div className="mt-10 md:mt-14">
            <div className="flex items-start justify-between gap-6 border-b border-foreground/10 pb-8 md:pb-10">
              <div><h2 className="font-display text-2xl font-medium md:text-3xl">Hosted by {property.host.name}</h2><p className="mt-2 text-sm text-muted-foreground">{property.guests} guests · {property.bedrooms} bedrooms · {property.baths} baths · Hosting for {property.host.years} years</p></div>
              {property.host.avatar && <img src={property.host.avatar} alt={property.host.name} className="h-14 w-14 rounded-full object-cover md:h-16 md:w-16" />}
            </div>
            <div className="border-b border-foreground/10 py-8 md:py-10"><p className="text-base leading-relaxed text-foreground/90 md:text-lg">{property.description}</p></div>
            <div className="border-b border-foreground/10 py-8 md:py-10"><h3 className="mb-6 font-display text-xl md:text-2xl">What this place offers</h3><div className="grid gap-y-4 sm:grid-cols-2">{property.amenities.map((amenity) => { const Icon = AMENITY_ICONS[amenity] || Wifi; return <div key={amenity} className="flex items-center gap-4"><Icon size={18} className="text-primary" /><span className="text-sm">{amenity}</span></div>; })}</div></div>
            <div className="border-b border-foreground/10 py-8 md:py-10"><h3 className="mb-6 font-display text-xl md:text-2xl">House rules</h3><div className="grid gap-5 sm:grid-cols-2">{HOUSE_RULES.map((rule) => { const Icon = RULE_ICONS[rule.icon] || IdCard; return <div key={rule.label} className="flex items-start gap-4"><Icon size={18} className="mt-0.5 text-primary" /><div><p className="text-sm font-medium">{rule.label}</p><p className="text-sm text-muted-foreground">{rule.value}</p></div></div>; })}</div></div>
            <div className="py-8 md:py-10"><h3 className="mb-4 font-display text-xl md:text-2xl">Frequently asked</h3><Accordion type="single" collapsible>{FAQS.map((item, index) => <AccordionItem key={item.q} value={`faq-${index}`}><AccordionTrigger className="font-display text-base hover:no-underline md:text-lg">{item.q}</AccordionTrigger><AccordionContent className="text-sm text-muted-foreground md:text-base">{item.a}</AccordionContent></AccordionItem>)}</Accordion></div>
          </div>

          <div className="mt-10 md:hidden">
            <BookingWidget mobile />
            <div className="mt-5 flex items-center justify-around text-xs text-muted-foreground"><span className="inline-flex items-center gap-1.5"><Users size={12} /> {property.guests}</span><span className="inline-flex items-center gap-1.5"><BedDouble size={12} /> {property.bedrooms}</span><span className="inline-flex items-center gap-1.5"><Bath size={12} /> {property.baths}</span></div>
          </div>
        </div>

        <aside className="hidden self-start md:sticky md:top-[88px] md:block">
          <BookingWidget />
          <div className="mt-5 flex items-center justify-around text-xs text-muted-foreground"><span className="inline-flex items-center gap-1.5"><Users size={12} /> {property.guests}</span><span className="inline-flex items-center gap-1.5"><BedDouble size={12} /> {property.bedrooms}</span><span className="inline-flex items-center gap-1.5"><Bath size={12} /> {property.baths}</span></div>
        </aside>
      </section>

      <GalleryLightbox open={lightboxOpen} onOpenChange={setLightboxOpen} images={galleryImages} initialIndex={lightboxIndex} title={property.name} />
    </main>
  );
};

const PropertyDetailSkeleton = () => (
  <main className="mx-auto max-w-[1400px] animate-pulse px-5 py-10 md:px-10 md:py-16">
    <div className="h-3 w-24 rounded bg-muted" />
    <div className="mt-6 h-12 w-3/5 rounded bg-muted md:h-16" />
    <div className="mt-4 h-4 w-56 rounded bg-muted" />
    <div className="mt-10 grid gap-10 md:grid-cols-[minmax(0,1.62fr)_minmax(330px,0.88fr)] md:gap-14">
      <div className="space-y-5"><div className="h-[360px] bg-muted md:h-[520px]" /><div className="h-28 bg-muted" /><div className="h-40 bg-muted" /><div className="h-48 bg-muted" /></div>
      <div className="hidden h-[460px] bg-muted md:block" />
    </div>
  </main>
);

export default PropertyDetail;
